import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { api, ApiError } from '@/lib/api-client';
import { Avatar, Badge, Button, Card, CardContent, DropdownSelect, EmptyState, ErrorState, Input, MultiSelect, PageHeader, PasswordInput, SkeletonRow, Tabs, Textarea, useConfirm } from '@/components/ui';
import type { Role } from './RolesAdminPages';
import { TIMEZONE_OPTIONS, detectBrowserTimezone } from '@/lib/timezones';
import { DEFAULT_LANGUAGE, LANGUAGE_OPTIONS, normalizeLanguage } from '@/lib/languages';
import { useI18n } from '@/lib/i18n';

/**
 * Admin > Agents: list and full-page editor.
 *
 * Modelled on the captured Freshdesk screens (FRESHDESK_PARITY_GAP_PLAN.md §27):
 *   image70.png - Agents list: tabs with counts (Support Agents / Collaborators /
 *                 Deactivated), Name (avatar+email) / Roles / Groups / Last Seen
 *                 columns, hover pencil to edit, kebab for Deactivate/Delete.
 *   image71.png - full-page "Edit Agent": Cancel/Save in the header, stacked
 *                 label/field rows.
 *
 * Freshsales tab and Agent Type (Full time/Occasional)/seats/day passes are
 * intentionally dropped - this is an internal product with no seat limits and
 * no CRM agent type (explicit product decision, see §27 point 5/6).
 */

type AgentRole = Role;

type AgentGroupOption = {
  id: string;
  name: string;
};

type TicketVisibilityScope = 'all' | 'group' | 'assigned';

function scopesForRole(roleType?: string): TicketVisibilityScope[] {
  if (roleType === 'super_admin' || roleType === 'administrator') return ['all', 'group', 'assigned'];
  if (roleType === 'supervisor' || roleType === 'agent') return ['group', 'assigned'];
  if (roleType === 'collaborator') return ['group'];
  return [];
}

export type Agent = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  roleId: string;
  isActive: boolean;
  phone?: string | null;
  signature?: string | null;
  timezone?: string | null;
  language?: string | null;
  ticketVisibilityScope?: TicketVisibilityScope | null;
  lastLoginAt?: string | null;
  createdAt?: string;
  role?: AgentRole;
  groupMemberships?: Array<{ groupId: string; group: { id: string; name: string } }>;
  _count?: { assignedTickets: number };
};

const COLLABORATOR_ROLE_TYPE = 'collaborator';
const AGENT_ROLE_TYPES = new Set(['super_admin', 'administrator', 'supervisor', 'agent']);

type Translator = ReturnType<typeof useI18n>['t'];

const problem = (error: unknown, t: Translator) =>
  error instanceof ApiError ? error.message : t('common.requestFailed');

function ErrorBanner({ message, onRetry }: { message: string; onRetry?: () => void }) {
  if (!message) return null;
  return <ErrorState message={message} onRetry={onRetry} />;
}

function AdminHeader({
  breadcrumb,
  title,
  subtitle,
  actions,
}: {
  breadcrumb: string;
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  const { t } = useI18n();
  return (
    <PageHeader
      className="mb-0"
      breadcrumb={[{ label: t('nav.settings'), to: '/settings' }, { label: breadcrumb }]}
      title={title}
      description={subtitle}
      actions={actions}
    />
  );
}

function HelpPanel({ children }: { children: React.ReactNode }) {
  return (
    <aside className="space-y-4 text-sm leading-relaxed text-ink-muted 2xl:border-l 2xl:border-line 2xl:pl-6">
      {children}
    </aside>
  );
}

function relativeLastSeen(value: string | null | undefined, t: Translator): string {
  if (!value) return t('common.never');
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return t('common.never');
  const diffMs = Date.now() - then;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return t('common.justNow');
  if (minutes < 60) return t('common.minutesAgo', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('common.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  return t('common.daysAgo', { count: days });
}

function groupSummary(agent: Agent, t: Translator): string {
  const names = agent.groupMemberships?.map((membership) => membership.group.name) ?? [];
  if (names.length === 0) return t('agents.noGroups');
  if (names.length <= 2) return names.join(', ');
  return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
}

type Tab = 'support' | 'collaborators' | 'deactivated';

type SortKey = 'name' | 'lastSeen';

export function AgentsListPage() {
  const { t } = useI18n();
  const { confirm } = useConfirm();
  const [agents, setAgents] = React.useState<Agent[]>([]);
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [tab, setTab] = React.useState<Tab>('support');
  const [search, setSearch] = React.useState('');
  const [sortKey, setSortKey] = React.useState<SortKey>('name');
  const [busyId, setBusyId] = React.useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      setError('');
      setAgents(await api.get<Agent[]>('/admin/agents'));
    } catch (cause) {
      setError(problem(cause, t));
    } finally {
      setLoading(false);
    }
  }, [t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  // Partitioned client-side from the single GET /admin/agents response - no
  // dedicated backend filter endpoint for this (frontend-only task per scope).
  const support = agents.filter((agent) => agent.isActive && AGENT_ROLE_TYPES.has(agent.role?.type ?? ''));
  const collaborators = agents.filter((agent) => agent.isActive && agent.role?.type === COLLABORATOR_ROLE_TYPE);
  const deactivated = agents.filter((agent) => !agent.isActive);

  const bucket = tab === 'support' ? support : tab === 'collaborators' ? collaborators : deactivated;
  const filtered = search.trim()
    ? bucket.filter((agent) => {
        const needle = search.trim().toLowerCase();
        return (
          `${agent.firstName} ${agent.lastName}`.toLowerCase().includes(needle) ||
          agent.email.toLowerCase().includes(needle)
        );
      })
    : bucket;

  const visible = [...filtered].sort((a, b) => {
    if (sortKey === 'lastSeen') {
      return (b.lastLoginAt ? new Date(b.lastLoginAt).getTime() : 0) - (a.lastLoginAt ? new Date(a.lastLoginAt).getTime() : 0);
    }
    return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
  });

  async function setActive(agent: Agent, isActive: boolean) {
    setOpenMenuId(null);
    setBusyId(agent.id);
    try {
      await api.put(`/admin/agents/${agent.id}`, { isActive });
      await load();
    } catch (cause) {
      setError(problem(cause, t));
    } finally {
      setBusyId(null);
    }
  }

  async function remove(agent: Agent) {
    setOpenMenuId(null);
    if (!await confirm({
      title: t('agents.confirmDeleteTitle', { name: `${agent.firstName} ${agent.lastName}` }),
      description: t('agents.confirmDeleteDescription'),
      confirmLabel: t('agents.confirmDeleteLabel'),
    })) {
      return;
    }
    setBusyId(agent.id);
    try {
      await api.delete(`/admin/agents/${agent.id}`);
      await load();
    } catch (cause) {
      setError(problem(cause, t));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="animate-fade-in space-y-6">
      <AdminHeader
        breadcrumb={t('agents.title')}
        title={t('agents.title')}
        subtitle={t('agents.description')}
        actions={
          <Link
            to="/settings/agents/new"
            className="inline-flex h-10 items-center justify-center rounded-lg bg-brand-600 px-4 text-sm font-medium text-white shadow-card transition-all duration-150 hover:bg-brand-700 hover:shadow-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
          >
            {t('agents.new')}
          </Link>
        }
      />
      <ErrorBanner message={error} onRetry={() => void load()} />

      <div className="grid gap-6 2xl:grid-cols-[minmax(0,1fr)_20rem]">
        <Card className="overflow-hidden">
          <Tabs
            aria-label={t('agents.lifecycle')}
            className="px-5"
            value={tab}
            onChange={(value) => setTab(value as Tab)}
            items={[
              { value: 'support', label: t('agents.supportAgents'), count: support.length },
              { value: 'collaborators', label: t('agents.collaborators'), count: collaborators.length },
              { value: 'deactivated', label: t('agents.deactivated'), count: deactivated.length },
            ]}
          />

          <div className="border-b border-line px-5 py-3">
            <div className="relative w-full sm:w-72">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle"
                aria-hidden="true"
              />
              <Input
                placeholder={t('agents.searchPlaceholder')}
                aria-label={t('agents.searchAria')}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="h-9 w-full pl-9 pr-9"
              />
              {search && (
                <button
                  type="button"
                  aria-label={t('common.clearSearch')}
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-ink-subtle transition-colors hover:bg-surface-100 hover:text-ink"
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line bg-surface-50 px-5 py-2.5 text-sm">
            <label className="flex min-w-0 flex-wrap items-center gap-2 text-ink-muted">
              {t('common.sortBy')}
              <DropdownSelect
                options={[
                  { value: 'name', label: t('common.name') },
                  { value: 'lastSeen', label: t('common.lastSeen') },
                ]}
                value={sortKey}
                onChange={(value) => setSortKey(value as SortKey)}
                searchable={false}
                className="w-auto"
              />
            </label>
            <span className="text-ink-subtle">
              {visible.length === 1 ? t('agents.countOne', { count: visible.length }) : t('agents.countMany', { count: visible.length })}
            </span>
          </div>

          <CardContent className="divide-y divide-line p-0">
            {loading && <SkeletonRow rows={5} />}
            {!loading && visible.length === 0 && (
              <EmptyState
                title={search.trim() ? t('agents.noSearchResults') : t('agents.noAgentsView')}
                description={search.trim()
                  ? t('agents.tryDifferent')
                  : t('agents.useNewAgent')}
              />
            )}
            {visible.length > 0 && (
              <div
                className="hidden items-center gap-4 px-5 py-2 text-xs font-medium uppercase tracking-wide text-ink-subtle xl:grid"
                style={{ gridTemplateColumns: '1fr 8rem 14rem 6rem 5.5rem 5rem' }}
              >
                <span>{t('agents.agent')}</span>
                <span>{t('common.role')}</span>
                <span>{t('common.groups')}</span>
                <span>{t('common.tickets')}</span>
                <span>{t('common.lastSeen')}</span>
                <span className="text-right">{t('common.actions')}</span>
              </div>
            )}
            {visible.map((agent) => (
              <div
                key={agent.id}
                className="group relative grid items-center gap-4 p-5 transition-colors duration-150 hover:bg-brand-50/50 xl:grid-cols-[1fr_8rem_14rem_6rem_5.5rem_5rem]"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <Avatar name={`${agent.firstName} ${agent.lastName}`} id={agent.id} />
                  <div className="min-w-0">
                    <Link
                      to={`/settings/agents/${agent.id}`}
                      className="block max-w-full truncate font-semibold text-brand-700 transition-colors hover:text-brand-800 hover:underline"
                    >
                      {agent.firstName} {agent.lastName}
                    </Link>
                    {!agent.isActive && <Badge variant="neutral" className="ml-2">{t('agents.deactivated')}</Badge>}
                    <p className="truncate text-sm text-ink-muted">{agent.email}</p>
                  </div>
                </div>

                <span className="min-w-0 truncate text-sm text-ink-muted" title={agent.role?.name}>
                  {agent.role?.name ?? t('agents.noRole')}
                </span>
                <span className="min-w-0 truncate text-sm text-ink-muted" title={groupSummary(agent, t)}>
                  {groupSummary(agent, t)}
                </span>
                <span
                  className="min-w-0 truncate whitespace-nowrap text-sm text-ink-muted"
                  title={t('agents.openTicketsTitle')}
                >
                  {agent._count?.assignedTickets === 1
                    ? t('agents.ticketOne', { count: agent._count?.assignedTickets ?? 0 })
                    : t('agents.ticketMany', { count: agent._count?.assignedTickets ?? 0 })}
                </span>
                <span className="min-w-0 truncate whitespace-nowrap text-xs text-ink-subtle" title={t('common.lastSeen')}>
                  {relativeLastSeen(agent.lastLoginAt, t)}
                </span>

                <div className="flex items-center justify-end gap-2 opacity-80 transition-opacity duration-150 group-hover:opacity-100">
                  <Link
                    to={`/settings/agents/${agent.id}`}
                    aria-label={t('agents.moreActions', { name: `${agent.firstName} ${agent.lastName}` })}
                    title={t('agents.edit')}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-line-strong bg-surface text-ink shadow-card transition-all duration-150 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M3 21l3.6-1 12-12a1.5 1.5 0 0 0 0-2.1l-1.5-1.5a1.5 1.5 0 0 0-2.1 0l-12 12L3 21Z"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Link>

                  <div className="relative">
                    <button
                      type="button"
                      aria-label={t('agents.moreActions', { name: `${agent.firstName} ${agent.lastName}` })}
                      aria-expanded={openMenuId === agent.id}
                      disabled={busyId === agent.id}
                      onClick={() => setOpenMenuId((current) => (current === agent.id ? null : agent.id))}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-line-strong bg-surface text-ink shadow-card transition-all duration-150 hover:border-brand-300 hover:bg-brand-50 disabled:opacity-50"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <circle cx="12" cy="5" r="1.6" />
                        <circle cx="12" cy="12" r="1.6" />
                        <circle cx="12" cy="19" r="1.6" />
                      </svg>
                    </button>
                    {openMenuId === agent.id && (
                      <div
                        role="menu"
                        className="absolute right-0 z-10 mt-1 w-40 overflow-hidden rounded-lg border border-line bg-surface shadow-raised"
                      >
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => void remove(agent)}
                          className="block w-full px-3 py-2 text-left text-sm text-accent-700 transition-colors hover:bg-accent-50"
                        >
                          {t('common.delete')}
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => void setActive(agent, !agent.isActive)}
                          className="block w-full px-3 py-2 text-left text-sm text-ink transition-colors hover:bg-brand-50"
                        >
                          {agent.isActive ? t('agents.deactivate') : t('agents.reactivate')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
        <HelpPanel>
          <h2 className="text-base font-semibold text-ink">{t('agents.agentsVsCollaborators')}</h2>
          <p>
            {t('agents.agentsVsCollaboratorsBody')}
          </p>
          <h2 className="text-base font-semibold text-ink">{t('agents.deactivateVsDelete')}</h2>
          <p>
            {t('agents.deactivateVsDeleteBody')}
          </p>
        </HelpPanel>
      </div>
    </div>
  );
}

export function AgentEditPage() {
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [roles, setRoles] = React.useState<AgentRole[]>([]);
  const [groups, setGroups] = React.useState<AgentGroupOption[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = React.useState<string[]>([]);
  const [ticketVisibilityScope, setTicketVisibilityScope] = React.useState<TicketVisibilityScope | ''>('');
  const [, setAgent] = React.useState<Agent | null>(null);
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [timezone, setTimezone] = React.useState(() => detectBrowserTimezone());
  const [language, setLanguage] = React.useState<string>(DEFAULT_LANGUAGE);
  const [signature, setSignature] = React.useState('');
  const [roleId, setRoleId] = React.useState('');
  const [isActive, setActive] = React.useState(true);
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [saved, setSaved] = React.useState(false);
  const [tempPassword, setTempPassword] = React.useState('');
  const [tempPasswordBusy, setTempPasswordBusy] = React.useState(false);
  const [tempPasswordNotice, setTempPasswordNotice] = React.useState<{ kind: 'success' | 'error'; message: string } | null>(null);

  async function handleSetTemporaryPassword() {
    if (isNew || tempPasswordBusy) return;
    setTempPasswordBusy(true);
    setTempPasswordNotice(null);
    try {
      const result = await api.post<{ success: boolean; emailSent?: boolean }>(`/admin/agents/${id}/temporary-password`, { password: tempPassword });
      setTempPassword('');
      setTempPasswordNotice(result?.emailSent === false
        ? { kind: 'error', message: t('agents.tempPassword.successNotEmailed') }
        : { kind: 'success', message: t('agents.tempPassword.successEmailed') });
    } catch (caught) {
      setTempPasswordNotice({
        kind: 'error',
        message: caught instanceof ApiError && caught.message ? caught.message : t('agents.tempPassword.error'),
      });
    } finally {
      setTempPasswordBusy(false);
    }
  }

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        setError('');
        const [allRoles, availableGroups] = await Promise.all([
          api.get<AgentRole[]>('/admin/roles'),
          api.get<AgentGroupOption[]>('/admin/agents/group-options'),
        ]);
        if (cancelled) return;
        setRoles(allRoles);
        setGroups(availableGroups);

        if (!isNew) {
          const existing = await api.get<Agent>(`/admin/agents/${id}`);
          if (cancelled) return;
          setAgent(existing);
          setFirstName(existing.firstName);
          setLastName(existing.lastName);
          setEmail(existing.email);
          setPhone(existing.phone ?? '');
          setTimezone(existing.timezone ?? '');
          setLanguage(normalizeLanguage(existing.language));
          setSignature(existing.signature ?? '');
          setRoleId(existing.roleId);
          setTicketVisibilityScope(existing.ticketVisibilityScope ?? '');
          setActive(existing.isActive);
          setSelectedGroupIds((existing.groupMemberships ?? []).map((membership) => membership.groupId));
        }
      } catch (cause) {
        if (!cancelled) setError(problem(cause, t));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isNew, t]);

  // A role is assignable to an agent if it is active and either a genuine agent
  // role type or the collaborator type - parens matter here, && binds tighter
  // than || so this must group the isActive+agent-type check as one clause.
  const assignableRoles = roles.filter(
    (role) => (role.isActive && AGENT_ROLE_TYPES.has(role.type)) || role.type === COLLABORATOR_ROLE_TYPE,
  );
  const selectedRoleType = roles.find((role) => role.id === roleId)?.type;
  const availableScopes = scopesForRole(selectedRoleType);

  function changeRole(value: string) {
    setRoleId(value);
    const nextRoleType = roles.find((role) => role.id === value)?.type;
    if (ticketVisibilityScope && !scopesForRole(nextRoleType).includes(ticketVisibilityScope)) {
      setTicketVisibilityScope('');
    }
  }

  const scopeChoices = [
    { value: '', label: t('agents.scopeRoleDefault'), description: t('agents.scopeRoleDefaultHelp') },
    ...availableScopes.map((scope) => ({
      value: scope,
      label: t(scope === 'all' ? 'agents.scopeAll' : scope === 'group' ? 'agents.scopeGroup' : 'agents.scopeAssigned'),
      description: t(scope === 'all' ? 'agents.scopeAllHelp' : scope === 'group' ? 'agents.scopeGroupHelp' : 'agents.scopeAssignedHelp'),
    })),
  ];

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      setError('');
      setSaved(false);
      if (isNew) {
        const created = await api.post<Agent>('/admin/agents', {
          firstName,
          lastName,
          email,
          password,
          roleId,
          ticketVisibilityScope: ticketVisibilityScope || null,
          groupIds: selectedGroupIds,
          timezone,
          language,
        });
        // Stay consistent with the roles editor: land on the persisted record
        // instead of bouncing back to the list with no confirmation.
        navigate(`/settings/agents/${created.id}`, { replace: true });
        setSaved(true);
      } else {
        const updated = await api.put<Agent>(`/admin/agents/${id}`, {
          firstName,
          lastName,
          email,
          phone: phone || undefined,
          timezone: timezone || undefined,
          language,
          signature: signature || undefined,
          roleId,
          ticketVisibilityScope: ticketVisibilityScope || null,
          isActive,
          groupIds: selectedGroupIds,
        });
        setAgent(updated);
        setSaved(true);
      }
    } catch (cause) {
      setError(problem(cause, t));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="animate-fade-in space-y-6" onSubmit={save}>
      <AdminHeader
        breadcrumb={t('agents.title')}
        title={isNew ? t('agents.newTitle') : t('agents.editTitle')}
        actions={
          <>
            <Button type="button" variant="outline" onClick={() => navigate('/settings/agents')}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={saving || loading}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </>
        }
      />
      <ErrorBanner message={error} />
      {saved && (
        <div
          role="status"
          className="animate-fade-in rounded-xl border border-success-500/20 bg-success-50 px-4 py-3 text-sm text-success-700"
        >
          {t('agents.saved')}
        </div>
      )}

      <div className="grid gap-6 2xl:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-6">
          <Card>
            <CardContent className="space-y-5 p-6">
              <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-center">
                <label htmlFor="agent-first-name" className="text-sm font-semibold text-ink">
                  {t('agents.firstName')} <span className="text-accent-500">*</span>
                </label>
                <Input
                  id="agent-first-name"
                  required
                  value={firstName}
                  onChange={(event) => setFirstName(event.target.value)}
                />
              </div>

              <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-center">
                <label htmlFor="agent-last-name" className="text-sm font-semibold text-ink">
                  {t('agents.lastName')} <span className="text-accent-500">*</span>
                </label>
                <Input
                  id="agent-last-name"
                  required
                  value={lastName}
                  onChange={(event) => setLastName(event.target.value)}
                />
              </div>

              <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-center">
                <label htmlFor="agent-email" className="text-sm font-semibold text-ink">
                  {t('agents.email')} <span className="text-accent-500">*</span>
                </label>
                <Input
                  id="agent-email"
                  type="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>

              {!isNew && (
                <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-start">
                  <label htmlFor="agent-temp-password" className="pt-2 text-sm font-semibold text-ink">
                    {t('agents.tempPassword.title')}
                  </label>
                  <div>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <PasswordInput
                        id="agent-temp-password"
                        autoComplete="new-password"
                        minLength={12}
                        value={tempPassword}
                        onChange={(event) => setTempPassword(event.target.value)}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        disabled={tempPasswordBusy || tempPassword.length < 12}
                        onClick={() => void handleSetTemporaryPassword()}
                      >
                        {tempPasswordBusy ? t('agents.tempPassword.saving') : t('agents.tempPassword.button')}
                      </Button>
                    </div>
                    <p className="mt-1.5 text-xs text-ink-subtle">{t('agents.tempPassword.help')}</p>
                    {tempPasswordNotice && (
                      <p
                        role={tempPasswordNotice.kind === 'error' ? 'alert' : 'status'}
                        className={tempPasswordNotice.kind === 'error' ? 'mt-1.5 text-xs text-danger-600' : 'mt-1.5 text-xs text-success-700'}
                      >
                        {tempPasswordNotice.message}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {isNew && (
                <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-start">
                  <label htmlFor="agent-password" className="pt-2 text-sm font-semibold text-ink">
                    {t('agents.temporaryPassword')} <span className="text-accent-500">*</span>
                  </label>
                  <div>
                    <PasswordInput
                      id="agent-password"
                      minLength={12}
                      required
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                    />
                    <p className="mt-1.5 text-xs text-ink-subtle">
                      {t('agents.passwordHelp')}
                    </p>
                  </div>
                </div>
              )}

              <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-center">
                <label htmlFor="agent-phone" className="text-sm font-semibold text-ink">
                  {t('agents.phone')}
                </label>
                <Input id="agent-phone" value={phone} onChange={(event) => setPhone(event.target.value)} />
              </div>

              <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-start">
                <label htmlFor="agent-timezone" className="pt-2 text-sm font-semibold text-ink">
                  {t('agents.timezone')}
                </label>
                <div>
                  <DropdownSelect
                    options={TIMEZONE_OPTIONS}
                    value={timezone}
                    onChange={setTimezone}
                    placeholder={t('agents.selectTimezone')}
                  />
                  <p className="mt-1.5 text-xs text-ink-subtle">
                    {t('agents.timezoneHelp')}
                  </p>
                </div>
              </div>

              <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-start">
                <label htmlFor="agent-language" className="pt-2 text-sm font-semibold text-ink">
                  {t('agents.language')}
                </label>
                <div>
                  <DropdownSelect
                    options={LANGUAGE_OPTIONS}
                    value={language}
                    onChange={setLanguage}
                    placeholder={t('agents.selectLanguage')}
                  />
                  <p className="mt-1.5 text-xs text-ink-subtle">
                    {t('agents.languageHelp')}
                  </p>
                </div>
              </div>

              <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-start">
                <label htmlFor="agent-signature" className="pt-2 text-sm font-semibold text-ink">
                  {t('agents.emailSignature')}
                </label>
                <Textarea
                  id="agent-signature"
                  rows={4}
                  placeholder={t('agents.signaturePlaceholder')}
                  value={signature}
                  onChange={(event) => setSignature(event.target.value)}
                />
              </div>

              <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-start">
                <label htmlFor="agent-role" className="pt-2 text-sm font-semibold text-ink">
                  {t('agents.roleRequired')} <span className="text-accent-500">*</span>
                </label>
                <div>
                  <DropdownSelect
                    options={[
                      { value: '', label: t('agents.selectRole') },
                      ...assignableRoles.map((role) => ({ value: role.id, label: role.name })),
                    ]}
                    value={roleId}
                    onChange={changeRole}
                  />
                  <p className="mt-1.5 text-xs text-ink-subtle">
                    {t('agents.roleHelp')}{' '}
                    <Link to="/settings/roles" className="text-brand-700 hover:underline">
                      {t('agents.manageRoles')}
                    </Link>
                  </p>
                </div>
              </div>

              <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-start">
                <span className="pt-2 text-sm font-semibold text-ink">{t('agents.ticketVisibility')}</span>
                <fieldset>
                  <legend className="sr-only">{t('agents.ticketVisibility')}</legend>
                  <div className="space-y-2">
                    {scopeChoices.map((choice) => (
                      <label
                        key={choice.value || 'role-default'}
                        className="flex cursor-pointer items-start gap-2 rounded-lg border border-line px-3 py-2.5 transition-colors hover:border-brand-300 hover:bg-brand-50/40"
                      >
                        <input
                          type="radio"
                          name="agent-ticket-visibility"
                          value={choice.value}
                          checked={ticketVisibilityScope === choice.value}
                          onChange={() => setTicketVisibilityScope(choice.value as TicketVisibilityScope | '')}
                          className="mt-0.5 h-4 w-4 border-line-strong text-brand-600 transition focus:ring-2 focus:ring-brand-500"
                        />
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-ink">{choice.label}</span>
                          <span className="block text-xs leading-relaxed text-ink-subtle">{choice.description}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                  <p className="mt-1.5 text-xs text-ink-subtle">
                    {selectedRoleType === COLLABORATOR_ROLE_TYPE
                      ? t('agents.scopeCollaboratorHelp')
                      : t('agents.ticketVisibilityHelp')}
                  </p>
                </fieldset>
              </div>

              <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-start">
                <label className="pt-2 text-sm font-semibold text-ink">
                  {t('agents.assignGroups')}
                </label>
                <div>
                  <MultiSelect
                    options={groups.map((group) => ({ value: group.id, label: group.name }))}
                    value={selectedGroupIds}
                    onChange={setSelectedGroupIds}
                    placeholder={t('agents.selectGroups')}
                    loading={loading}
                    searchable
                    aria-label={t('agents.assignGroups')}
                  />
                  <p className="mt-1.5 text-xs text-ink-subtle">
                    {t('agents.assignGroupsHelp')}{' '}
                    <Link to="/settings/groups" className="text-brand-700 hover:underline">
                      {t('agents.manageGroups')}
                    </Link>
                  </p>
                </div>
              </div>

              {!isNew && (
                <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-center">
                  <span className="text-sm font-semibold text-ink">{t('agents.status')}</span>
                  <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-ink">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-line-strong text-brand-600 transition focus:ring-2 focus:ring-brand-500"
                      checked={isActive}
                      onChange={(event) => setActive(event.target.checked)}
                    />
                    {t('agents.activeHelp')}
                  </label>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        <HelpPanel>
          <h2 className="text-base font-semibold text-ink">{t('agents.about')}</h2>
          <p>
            {t('agents.aboutBody')}
          </p>
        </HelpPanel>
      </div>
    </form>
  );
}
